// Tokens de los 5 estilos de página pública (plan §2b-bis). Un solo componente de página los renderiza;
// el estilo, el acento de la cuenta y la fuente opcional se resuelven aquí a variables CSS. Nunca se guarda
// CSS por cuenta: la base solo admite el nombre del estilo, un hex estricto y una fuente de una lista cerrada.
import { INK_DARK, inkOn, isHexColor, readableAccent, rgba } from './contrast.ts';

export const EVENT_STYLES = ['limpio', 'editorial', 'bloque', 'noche', 'institucional'] as const;
export type EventStyle = (typeof EVENT_STYLES)[number];

export const FONT_IDS = ['geist', 'instrument-serif', 'bricolage-grotesque', 'ibm-plex-sans'] as const;
export type FontId = (typeof FONT_IDS)[number];

export type PageLayout = 'split' | 'stack' | 'poster' | 'band';
export type BackgroundKind = 'dots' | 'paper' | 'ribbons' | 'aurora' | 'topo';

export const FONT_STACKS: Record<FontId | 'mono', string> = {
  geist: "'Geist Variable', 'Geist', system-ui, sans-serif",
  'instrument-serif': "'Instrument Serif', Georgia, serif",
  'bricolage-grotesque': "'Bricolage Grotesque Variable', 'Bricolage Grotesque', system-ui, sans-serif",
  'ibm-plex-sans': "'IBM Plex Sans', system-ui, sans-serif",
  mono: "'Geist Mono Variable', 'Geist Mono', ui-monospace, monospace",
};

interface StyleDef {
  label: string;
  /** Para qué evento conviene (lo muestra el selector de estilo). */
  hint: string;
  layout: PageLayout;
  background: BackgroundKind;
  backgroundLabel: string;
  defaultAccent: string;
  bg: string;
  surface: string;
  line: string;
  ink: string;
  muted: string;
  display: FontId;
  body: FontId;
  mono: FontId | 'mono';
  titleSize: string;
  titleWeight: number;
  h2Size: string;
  h2Weight: number;
  tracking: string;
  radius: string;
  buttonRadius: string;
  dark: boolean;
}

export const STYLE_DEFS: Record<EventStyle, StyleDef> = {
  limpio: {
    label: 'Limpio', hint: 'Cualquier evento', layout: 'split', background: 'dots', backgroundLabel: 'Trama de puntos',
    defaultAccent: '#2F5BEA', bg: '#FFFFFF', surface: '#F6F6F4', line: '#E7E5E4', ink: '#121212', muted: '#5C5C5C',
    display: 'geist', body: 'geist', mono: 'mono', titleSize: 'clamp(34px, 4.4cqw, 54px)', titleWeight: 600,
    h2Size: '22px', h2Weight: 600, tracking: '-0.03em', radius: '18px', buttonRadius: '12px', dark: false,
  },
  editorial: {
    label: 'Editorial', hint: 'Charlas, seminarios, cultura', layout: 'stack', background: 'paper', backgroundLabel: 'Papel editorial',
    defaultAccent: '#B4532A', bg: '#FAF7F2', surface: '#F2ECE3', line: '#E3DACD', ink: '#1F1A17', muted: '#5E554D',
    display: 'instrument-serif', body: 'geist', mono: 'geist', titleSize: 'clamp(44px, 6.4cqw, 84px)', titleWeight: 400,
    h2Size: '30px', h2Weight: 400, tracking: '-0.01em', radius: '4px', buttonRadius: '4px', dark: false,
  },
  bloque: {
    label: 'Bloque', hint: 'Ferias, juventud, festivales', layout: 'poster', background: 'ribbons', backgroundLabel: 'Cintas',
    defaultAccent: '#FF6B35', bg: '#FFFFFF', surface: '#F5F2EC', line: '#E6E0D6', ink: '#141414', muted: '#4D4D4D',
    display: 'bricolage-grotesque', body: 'bricolage-grotesque', mono: 'bricolage-grotesque',
    titleSize: 'clamp(46px, 7.2cqw, 96px)', titleWeight: 800, h2Size: '26px', h2Weight: 800, tracking: '-0.04em',
    radius: '24px', buttonRadius: '999px', dark: false,
  },
  noche: {
    label: 'Noche', hint: 'Lanzamientos, tecnología, after office', layout: 'split', background: 'aurora', backgroundLabel: 'Aurora',
    defaultAccent: '#7CF0B4', bg: '#0D0E12', surface: '#16181E', line: '#272A33', ink: '#F3F4F6', muted: '#A3A7B0',
    display: 'geist', body: 'geist', mono: 'mono', titleSize: 'clamp(34px, 4.4cqw, 54px)', titleWeight: 600,
    h2Size: '22px', h2Weight: 600, tracking: '-0.03em', radius: '16px', buttonRadius: '12px', dark: true,
  },
  institucional: {
    label: 'Institucional', hint: 'Municipios, gobiernos regionales, consultas', layout: 'band', background: 'topo', backgroundLabel: 'Topografía',
    defaultAccent: '#0B4F8A', bg: '#FFFFFF', surface: '#F3F5F8', line: '#D8DEE7', ink: '#0F1B2D', muted: '#4A5568',
    display: 'ibm-plex-sans', body: 'ibm-plex-sans', mono: 'ibm-plex-sans', titleSize: 'clamp(32px, 3.8cqw, 46px)',
    titleWeight: 600, h2Size: '22px', h2Weight: 600, tracking: '-0.01em', radius: '8px', buttonRadius: '6px', dark: false,
  },
};

export function isEventStyle(value: unknown): value is EventStyle {
  return typeof value === 'string' && (EVENT_STYLES as readonly string[]).includes(value);
}

export interface ThemeInput {
  style: EventStyle;
  /** Acento de la cuenta (#rrggbb). Si falta o es inválido, se usa el del estilo. */
  accent?: string | null;
  /** Fuente de la cuenta. Si falta, manda el estilo. Solo reemplaza la tipografía de títulos. */
  font?: FontId | null;
}

export interface EventTheme {
  style: EventStyle;
  layout: PageLayout;
  background: BackgroundKind;
  dark: boolean;
  colors: {
    bg: string; surface: string; line: string; ink: string; muted: string;
    accent: string; onAccent: string; accentText: string;
    buttonBg: string; buttonInk: string; bar: string;
    coverBg: string; coverA: string; coverB: string;
  };
  /** Variables CSS listas para `style` del contenedor de la página. */
  vars: Record<`--${string}`, string>;
}

export function resolveEventTheme(input: ThemeInput): EventTheme {
  const def = STYLE_DEFS[input.style];
  const accent = isHexColor(input.accent) ? input.accent.toUpperCase() : def.defaultAccent;
  const onAccent = inkOn(accent);
  const accentText = readableAccent(accent, def.bg, def.ink);

  let buttonBg = accent;
  let buttonInk = onAccent;
  let bar = accent;
  let coverBg = accent;
  let coverA = onAccent === INK_DARK ? 'rgba(20,20,20,0.88)' : 'rgba(255,255,255,0.92)';
  let coverB = onAccent === INK_DARK ? 'rgba(20,20,20,0.25)' : 'rgba(255,255,255,0.35)';
  let rule = rgba(accent, 0.14);
  let ruleStrong = rgba(accent, 0.22);

  switch (input.style) {
    case 'editorial':
      coverBg = '#E8DCCB'; coverA = accent; coverB = def.ink;
      rule = rgba(def.ink, 0.07); ruleStrong = rgba(accent, 0.25);
      break;
    case 'bloque':
      // El acento es el afiche; los botones van en tinta para no competir con él.
      buttonBg = def.ink; buttonInk = '#FFFFFF'; bar = def.ink;
      coverA = onAccent; coverB = 'rgba(255,255,255,0.5)';
      break;
    case 'noche':
      coverBg = def.surface; coverA = accent; coverB = '#343945';
      break;
    case 'institucional':
      coverBg = '#E6EDF6'; coverA = accent; coverB = '#9DB2CC';
      rule = rgba(accent, 0.12);
      break;
    case 'limpio':
      break;
  }

  const display = input.font ?? def.display;
  const colors = {
    bg: def.bg, surface: def.surface, line: def.line, ink: def.ink, muted: def.muted,
    accent, onAccent, accentText, buttonBg, buttonInk, bar, coverBg, coverA, coverB,
  };

  const vars: EventTheme['vars'] = {
    '--ev-bg': def.bg,
    '--ev-surface': def.surface,
    '--ev-line': def.line,
    '--ev-ink': def.ink,
    '--ev-muted': def.muted,
    '--ev-accent': accent,
    '--ev-on-accent': onAccent,
    '--ev-accent-text': accentText,
    '--ev-button-bg': buttonBg,
    '--ev-button-ink': buttonInk,
    '--ev-bar': bar,
    '--ev-cover-bg': coverBg,
    '--ev-cover-a': coverA,
    '--ev-cover-b': coverB,
    '--ev-font-display': FONT_STACKS[display],
    '--ev-font-body': FONT_STACKS[def.body],
    '--ev-font-mono': FONT_STACKS[def.mono],
    '--ev-title-size': def.titleSize,
    '--ev-title-weight': String(def.titleWeight),
    '--ev-h2-size': def.h2Size,
    '--ev-h2-weight': String(def.h2Weight),
    '--ev-tracking': def.tracking,
    '--ev-radius': def.radius,
    '--ev-button-radius': def.buttonRadius,
    '--ev-bg-dot': rgba(def.ink, 0.16),
    '--ev-bg-rule': rule,
    '--ev-bg-rule-strong': ruleStrong,
    '--ev-bg-glow-a': rgba(accent, 0.3),
    '--ev-bg-glow-b': rgba('#7C8CFF', 0.22),
    '--ev-bg-star': 'rgba(255,255,255,0.55)',
    '--ev-bg-ribbon-a': rgba(onAccent, 0.14),
    '--ev-bg-ribbon-b': rgba(onAccent, 0.09),
  };

  return { style: input.style, layout: def.layout, background: def.background, dark: def.dark, colors, vars };
}
